import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga';
import Quiz from '../Components/quiz/Quiz';
import { QUIZ_SLUG } from '../Constants';
import useQuiz from '../Components/quiz/useQuiz';
import { saveScoreInDatabase } from '../dao/quiz';
import { fetchPageBySlug } from '../dao/pages';
import { determineKnownLocale } from './general/LanguageDetector';
import { useOktaAuth } from '@okta/okta-react';

/**
 * Home
 * @return {JSX.element} ...
 */
export default function Home() {
  const quizExpired = false;
  const daySlug = QUIZ_SLUG;

  const { i18n } = useTranslation();
  const language = determineKnownLocale(i18n.language);
  const { t } = useTranslation('quiz');
  const { authState } = useOktaAuth();

  const [quizFinished, setQuizFinished] = useState(false);
  const [noAnswers, setNoAnswers] = useState(false);
  const [notAllQuestionsAnswered, setNotAllQuestionsAnswered] = useState(false);
  const [errors, setErrors] = useState([]);
  const [page, setPage] = useState(null);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [questions] = useQuiz(`${daySlug}-${language}`);

  useEffect(() => {
    const fetchPage = async (slug) => {
      try {
        setPage(await fetchPageBySlug(slug, language));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
    };

    // Fetch WordPress page data for slug home
    fetchPage('home');
  }, [language]);

  /**
   * Submit the quiz, used in the Quiz component
   *
   * @param {array} selectedAnswers - The selected answers
   * @param {array} feedbackResponses - The feedback responses
   * @param {object} companyData - The selected company data
   */
  async function submitQuiz(selectedAnswers, feedbackResponses = [], companyData = {}) {
    setErrors('');
    setIsSubmitting(true);

    // Reset states
    setNoAnswers(false);
    setNotAllQuestionsAnswered(false);

    // Filter out answers for informational questions (questions with no answer options)
    const questionsWithAnswers = questions.filter(q => {
      const validAnswers = q?.answers?.filter(a => a?.text && a.text.trim() !== '') || [];
      return validAnswers.length > 0 && q?.type !== 'feedback';
    });

    // Only check answers from questions that actually have answer options
    const actualAnswers = selectedAnswers.filter(sa => {
      const question = questions.find(q => q.id === sa.question);
      const validAnswers = question?.answers?.filter(a => a?.text && a.text.trim() !== '') || [];
      return validAnswers.length > 0;
    });

    if (!actualAnswers.length && questionsWithAnswers.length > 0) {
      setNoAnswers(true);
      setIsSubmitting(false);
    } else if (actualAnswers.length !== questionsWithAnswers.length) {
      setNotAllQuestionsAnswered(true);
      setIsSubmitting(false);
    } else {
      // Extract Okta user info
      const userInfo = authState?.idToken?.claims || {};
      const userData = {
        email: userInfo.email || null,
        oktaorgname: userInfo.oktaorgname || null,
        companyname: userInfo.companyname || null,
        organization: userInfo.organization || null,
        selectedCompany: companyData.company || null,
      };

      // Only submit answers for questions that actually have answer options
      saveScoreInDatabase(actualAnswers, `${QUIZ_SLUG}-${language}`, language, userData, feedbackResponses).then(() => {
        setNoAnswers(false);
        setQuizFinished(true);
        setIsSubmitting(false);

        let correctAnswerCount = 0;
        actualAnswers.forEach((selectedAnswer) => {
          // All answers are counted as correct when answer === correctAnswerIndex
          // (for "all answers same points" questions, we already set correctAnswerIndex to the selected answer)
          if (selectedAnswer.answer === selectedAnswer.correctAnswerIndex) {
            correctAnswerCount++;
          }
        });

        // Subtract the count of "all answers same points" questions from the total
        // These questions are always correct but shouldn't count towards the score
        const allAnswersSamePointsCount = questions.filter(q => {
          const validAnswers = q?.answers?.filter(a => a?.text && a.text.trim() !== '') || [];
          return validAnswers.length > 0 && 
                 q?.type !== 'feedback' && 
                 validAnswers.every(a => a.points === validAnswers[0]?.points);
        }).length;

        setCorrectAnswers(correctAnswerCount - allAnswersSamePointsCount);
      });

      // Send a event to Google Analytics
      ReactGA.event({
        category: 'Quiz',
        action: 'Successful form submission',
      });
    }
  }

  return (
    <div className="quiz-day">
      {page && !quizStarted && (
        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-5 offset-lg-1">
              <div className="page-content">
                <div
                  dangerouslySetInnerHTML={{
                    __html: page?.content?.rendered,
                  }}
                />
                <button
                  className="button button--pagination button--start"
                  onClick={() => {
                    setQuizStarted(true);
                  }}
                >
                  {t('startLearning')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {quizStarted && (
        <Quiz
          questions={questions}
          submitQuiz={submitQuiz}
          noAnswers={noAnswers}
          quizFinished={quizFinished}
          quizExpired={quizExpired}
          notAllQuestionsAnswered={notAllQuestionsAnswered}
          errors={errors}
          correctAnswers={correctAnswers}
          confirmationData={page?.acf}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}
